import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MUILink,
  Paper,
  Slide,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import { Close, CookieOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  CATEGORIES,
  CONSENT_OPEN_EVENT,
  DEFAULT_PREFERENCES,
  readConsent,
  writeConsent
} from '../utils/consent';

const CookieConsent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [bannerOpen, setBannerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    const existing = readConsent();
    if (existing) setPrefs(existing.preferences);
    else setBannerOpen(true);
  }, []);

  // Footer "Cookie settings" link reopens the dialog for withdrawal or change.
  useEffect(() => {
    const onOpen = () => {
      const existing = readConsent();
      setPrefs(existing ? existing.preferences : DEFAULT_PREFERENCES);
      setDialogOpen(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  const commit = useCallback((preferences) => {
    writeConsent(preferences);
    setPrefs(preferences);
    setBannerOpen(false);
    setDialogOpen(false);
  }, []);

  const acceptAll = () => commit({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => commit({ necessary: true, analytics: false, marketing: false });
  const saveChoices = () => commit(prefs);

  return (
    <>
      <Slide direction="up" in={bannerOpen} mountOnEnter unmountOnExit>
        <Box
          role="region"
          aria-label="Cookie consent"
          // Above the header, but below the Drawer (1200) and the preferences
          // Modal (1300) — the banner stays open behind the dialog it launches.
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar + 2,
            p: { xs: 1.5, md: 2.5 }
          }}
        >
          <Container maxWidth="lg" disableGutters>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 4,
                ...theme.surfaces.glass,
                boxShadow: `0 20px 60px ${alpha('#000', 0.18)}`,
                borderColor: alpha(theme.palette.primary.main, 0.2)
              }}
            >
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={{ xs: 2.5, lg: 4 }}
                alignItems={{ xs: 'stretch', lg: 'center' }}
              >
                <Stack direction="row" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 2,
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.main',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }}
                  >
                    <CookieOutlined />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    {/* component="p": the region is already named, and a heading here
                        would land out of order at the end of the document. */}
                    <Typography variant="subtitle1" component="p" sx={{ fontWeight: 800, mb: 0.5 }}>
                      We value your privacy
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We use strictly necessary cookies to make this site work. With your permission we would also like to
                      use analytics and marketing cookies to improve it. You can change your choice at any time. Read our{' '}
                      <MUILink component={RouterLink} to="/privacy" sx={{ fontWeight: 600 }}>
                        Privacy Policy
                      </MUILink>
                      .
                    </Typography>
                  </Box>
                </Stack>

                {/* Accept and Reject carry equal weight, as the GDPR requires. */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ flexShrink: 0, width: { xs: '100%', lg: 'auto' } }}
                >
                  <Button
                    variant="text"
                    onClick={() => setDialogOpen(true)}
                    sx={{ fontWeight: 700, color: 'text.secondary', order: { xs: 3, sm: 1 } }}
                  >
                    Customise
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={rejectAll}
                    sx={{ fontWeight: 700, minWidth: 140, order: { xs: 2, sm: 2 } }}
                  >
                    Reject all
                  </Button>
                  <Button
                    variant="contained"
                    onClick={acceptAll}
                    sx={{ fontWeight: 700, minWidth: 140, order: { xs: 1, sm: 3 } }}
                  >
                    Accept all
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Container>
        </Box>
      </Slide>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        aria-labelledby="cookie-preferences-title"
        slotProps={{ paper: { sx: { borderRadius: { xs: 0, sm: 4 } } } }}
      >
        <DialogTitle
          id="cookie-preferences-title"
          sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          Cookie preferences
          <IconButton onClick={() => setDialogOpen(false)} aria-label="Close cookie preferences">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose which categories you allow. Strictly necessary cookies are always active because the site cannot work
            without them. Your choice is stored on this device and you can change it whenever you like.
          </Typography>

          <Stack divider={<Divider flexItem />} spacing={2}>
            {CATEGORIES.map((category) => (
              <Box
                key={category.id}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pt: category.id === 'necessary' ? 0 : 2 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>{category.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                </Box>
                <Switch
                  checked={category.required ? true : Boolean(prefs[category.id])}
                  disabled={category.required}
                  onChange={(e) => setPrefs((p) => ({ ...p, [category.id]: e.target.checked }))}
                  // MUI v7 routes input attributes through slotProps; inputProps is ignored.
                  slotProps={{ input: { 'aria-label': `${category.label} cookies` } }}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={rejectAll} sx={{ fontWeight: 700 }}>
            Reject all
          </Button>
          <Button variant="outlined" onClick={acceptAll} sx={{ fontWeight: 700 }}>
            Accept all
          </Button>
          <Button variant="contained" onClick={saveChoices} sx={{ fontWeight: 700 }}>
            Save choices
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
